import React, { useState } from 'react';
import { formatCurrency, toQuarter, toYear } from '../../helpers/format';
import useIncomeStatement from '../../hooks/useIncomeStatement';
import Table from '../Table/Table';
import style from './StockDetails.module.css';

const IncomeStatementTable = ({ stock, period }) => {
  const { incomeStatement } = useIncomeStatement(stock.id, period);
  const [showDetails, setShowDetails] = useState(false);
  const barChart = miniBarChart(incomeStatement?.current, showDetails);
  let change;

  const rows = barChart && barChart.bars.map((bar) => {
    return (
      <tr key={bar.key}>
        <th>{bar.label}</th>
        <td>
          {bar.value < 0 ? '-' : ''}${formatCurrency(Math.abs(bar.value), { compact: true })}
        </td>
        <td className={`${style.miniBarChart} ${bar.expense && style.miniBarChartExpense} ${bar.value < 0 && style.miniBarChartNegative}`}>
          <span className={style.miniBarChartBar}
            style={{
              width: `${bar.width * 100}%`,
              marginLeft: `${bar.marginLeft * 100}%`
            }}></span>
          <span className={style.miniBarChartBorder}
            style={{ left: `${barChart.zeroAt * 100}%` }}></span>
        </td>
        {(change = changeInPercent(incomeStatement?.current[bar.key], incomeStatement?.previous[bar.key])) && null}
        <td className={change ? change.startsWith('-') ? style.negativeChange : style.positiveChange : style.noChangeAvailable}>
          {change || '-'}
        </td>
      </tr>
    );
  });

  if (incomeStatement?.current && Object.keys(incomeStatement.current).length === 0) { return null; }

  return (
    <Table>
      <thead>
        <tr>
          <td colSpan="3">{incomeStatement?.current && (period === 'quarterly' ? toQuarter(incomeStatement.current.date) : toYear(incomeStatement.current.date))}</td>
          <th width="5%">YOY</th>
        </tr>
      </thead>

      <tbody>
        {rows}

        <tr>
          <th>Profit Margin</th>
          <th colSpan={2}>
            {profitMargin(incomeStatement?.current)}
          </th>
          {(change = changeInPercent(incomeStatement?.current?.net_income / incomeStatement?.current?.total_revenue, incomeStatement?.previous?.net_income / incomeStatement?.previous?.total_revenue)) && null}
          <td className={change ? change.startsWith('-') ? style.negativeChange : style.positiveChange : style.noChangeAvailable}>
            {change || '-'}
          </td>
        </tr>

        {!showDetails && (
          <tr>
            <th colSpan={4}>
              <button onClick={() => setShowDetails(true)}>
                + Show details
              </button>
            </th>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

export default IncomeStatementTable;

function miniBarChart(incomeStatement, showDetails) {
  if (!incomeStatement) { return null; }

  const bars = [];
  const maxValue = incomeStatement['total_revenue'];
  const minValue = Math.abs(getMinValue(incomeStatement));
  const range = maxValue + minValue;
  let expensesMarginRight = 0;

  Object.keys(dataLabels).forEach((key) => {
    const value = incomeStatement[key];
    const label = dataLabels[key];
    const width = Math.abs(value) / range;

    if ((label.hidden && !showDetails) || value === 0) { return null; }

    let marginLeft = 0;
    if (label.expense && value < 0) {
      marginLeft = 1 - expensesMarginRight;
      expensesMarginRight -= width;
    } else if (label.expense && value > 0) {
      marginLeft = 1 - expensesMarginRight - width;
      expensesMarginRight += width;
    } else if (value < 0) {
      expensesMarginRight = 1 - ((minValue / range) - width);
      marginLeft = (minValue / range) - width;
    } else {
      expensesMarginRight = 1 - ((minValue / range) + width);
      marginLeft = minValue / range;
    }

    bars.push({
      key: key,
      label: label.label,
      value: value,
      width: width,
      marginLeft: marginLeft,
      expense: label.expense,
    });
  });

  return {
    zeroAt: minValue / range,
    minValue: minValue,
    maxValue: maxValue,
    bars: bars
  };
}

// get min value where expense is false
function getMinValue(incomeStatement) {
  let minValue = 0;
  Object.keys(dataLabels).forEach((key) => {
    const value = incomeStatement[key];
    const label = dataLabels[key];
    if (value < minValue && !label.expense) {
      minValue = value;
    }
  });
  return minValue;
}

function profitMargin(incomeStatement) {
  if (!incomeStatement) { return null; }
  const revenue = incomeStatement['total_revenue'];
  const netIncome = incomeStatement['net_income'];
  if (revenue === 0) { return '-'; }
  return `${((netIncome / revenue) * 100).toFixed(1)}%`;
}

function changeInPercent(currentValue, previousValue) {
  if (!currentValue || !previousValue) { return null; }
  const sign = currentValue > previousValue ? '+' : '';
  const change = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
  return `${sign}${change.toFixed(1)}%`;
}

const dataLabels = {
  'total_revenue': {
    label: 'Total Revenue',
    hidden: false,
    expense: false,
  },
  'cost_of_revenue': {
    label: 'Cost of Revenue',
    hidden: true,
    expense: true,
  },
  'gross_profit': {
    label: 'Gross Profit',
    hidden: false,
    expense: false,
  },
  'research_and_development': {
    label: 'Research & Development',
    hidden: true,
    expense: true,
  },
  'selling_general_and_admin': {
    label: 'Selling, General & Admin',
    hidden: true,
    expense: true,
  },
  'other_operating_expenses': {
    label: 'Other Operating Expenses',
    hidden: true,
    expense: true,
  },
  'operating_income': {
    label: 'Operating Income',
    hidden: true,
    expense: false,
  },
  'other_income_expense_net': {
    label: 'Other Income Expense Net',
    hidden: true,
    expense: false,
  },
  'ebit': {
    label: 'EBIT',
    hidden: false,
    expense: false,
  },
  'pretax_income': {
    label: 'Pretax Income',
    hidden: true,
    expense: false,
  },
  'income_tax': {
    label: 'Income Tax',
    hidden: true,
    expense: true,
  },
  'net_income': {
    label: 'Net Income',
    hidden: false,
    expense: false,
  },
};